/**
 * Insert-row-dialog voor de Tabel (nieuw). Geactiveerd via Alt+N (Cmd+N
 * is door Chrome bezet voor "Nieuw venster") of via rechtermuisknop op
 * een rij → "Regel invoegen".
 *
 * Pass A: concept-pad — type-zoek + naam → addConceptOnCanvas
 * met smart-placement (zie findEmptyPlacement.ts).
 * Pass B: relatie-pad — source-/target-picker uit huidige diagram +
 * matrix-gefilterde relatie-type-picker + optioneel label →
 * connectConcepts (+ patchRelation voor 't label).
 */

import type { Concept, ElementType, StereotypeBundle } from "@ae/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getRegistry,
  isElementTypeAllowedOnViewKind,
  resolveBilingual,
  validRelationTypesBetween,
} from "../../registry";
import { useWorkspaceData } from "../../state/workspace";
import { colorForConcept } from "../layerColors";

type Kind = "concept" | "relation" | "plantuml";

/**
 * Resultaat van een PlantUML-import — getoond als feedback in de dialog
 * na de import. Velden komen uit `ApplyDiff` in applyParseResult.ts.
 */
export interface PlantUmlImportDiff {
  conceptsCreated: number;
  conceptsUpdated: number;
  conceptsRemoved: number;
  relationsCreated: number;
  relationsUpdated: number;
  relationsRemoved: number;
}

interface InsertRowDialogProps {
  open: { kind: Kind } | null;
  onClose: () => void;
  onCreateConcept: (typeId: string, name: string, stereotypes?: string[]) => void;
  onCreateRelation: (
    sourceConceptId: string,
    targetConceptId: string,
    relationTypeId: string,
    label: string,
  ) => void;
  /**
   * Optioneel: PlantUML-snippet-tekst parsen + naar workspace toevoegen.
   * Wanneer afwezig wordt de PlantUML-tab niet getoond. Bij succes
   * returns een diff-summary; bij fail throwt 'ie met een leesbare
   * message die we in de dialog tonen.
   */
  onImportPlantUml?: (text: string) => Promise<PlantUmlImportDiff>;
  /**
   * Optioneel: relation-create die "nieuw concept" als endpoint
   * ondersteunt. Wanneer aanwezig schakelt de dialog een
   * "Bestaand/Nieuw"-toggle in per source/target-picker en gebruikt dan
   * deze callback i.p.v. `onCreateRelation`. De caller is
   * verantwoordelijk voor 't aanmaken van nieuwe concepten + de relatie
   * + tableOrder.
   */
  onCreateRelationFlex?: (
    source: RelationEndpoint,
    target: RelationEndpoint,
    relationTypeId: string,
    label: string,
  ) => Promise<void>;
}

export type RelationEndpoint =
  | { kind: "existing"; id: string }
  | { kind: "new"; typeId: string; name: string };

interface ConceptPickItem {
  id: string;
  typeId: string;
  label: string;
}

/**
 * Item in de Concept-tab-lijst — ofwel een pure base-type ofwel een
 * stereotype-op-base. `key` is uniek over de hele lijst (incl. polymorfe
 * stereotypes die meerdere keren voorkomen, één per applies_to-base).
 */
type InsertItem =
  | { kind: "type"; key: string; baseType: ElementType }
  | {
      kind: "stereotype";
      key: string;
      stereotype: StereotypeBundle;
      baseType: ElementType;
    };

/**
 * Display-label voor een lijst-entry: pure type → just label;
 * stereotype → `«Stereotype-label» (Base-label)`.
 */
function displayItemLabel(item: InsertItem, lang: "nl" | "en"): string {
  if (item.kind === "type") return resolveBilingual(item.baseType.label, lang);
  return `«${resolveBilingual(item.stereotype.label, lang)}» (${resolveBilingual(item.baseType.label, lang)})`;
}

export function InsertRowDialog({
  open,
  onClose,
  onCreateConcept,
  onCreateRelation,
  onImportPlantUml,
  onCreateRelationFlex,
}: InsertRowDialogProps) {
  const { i18n, t } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const revision = useWorkspaceData((s) => s.revision);
  const activeViewKind = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const diagram = useWorkspaceData.getState().store.getDiagram(activeDiagramId);
    return diagram?.viewKind ?? null;
  }, [activeDiagramId, revision]);

  const [kind, setKind] = useState<Kind>("concept");
  const [conceptSearch, setConceptSearch] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  // Wanneer de gebruiker een «Stereotype»-regel selecteert i.p.v. een
  // pure base-type, bewaren we de stereotype-id hier. handleSubmit geeft
  // 'm mee aan onCreateConcept zodat 't concept direct ontstaat met de
  // stereotype al toegepast. Pure base-type-selectie zet deze terug op null.
  const [selectedStereotypeId, setSelectedStereotypeId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [sourceSearch, setSourceSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [selectedRelationTypeId, setSelectedRelationTypeId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  // PlantUML-tab-state. `puText` = de snippet die de gebruiker plakt;
  // `puBusy` = parser/apply loopt; `puFeedback` = success-/fout-bericht
  // ná een import-poging.
  const [puText, setPuText] = useState("");
  const [puBusy, setPuBusy] = useState(false);
  const [puFeedback, setPuFeedback] = useState<
    { ok: true; diff: PlantUmlImportDiff } | { ok: false; error: string } | null
  >(null);
  // Relation-tab-state voor "nieuw concept als endpoint". Per picker een
  // mode-toggle. In "new"-mode kiest de gebruiker een type + naam i.p.v.
  // een bestaand concept te selecteren — handig wanneer endpoints nog
  // niet op het diagram staan.
  const [sourceMode, setSourceMode] = useState<"existing" | "new">("existing");
  const [targetMode, setTargetMode] = useState<"existing" | "new">("existing");
  const [sourceNewTypeId, setSourceNewTypeId] = useState<string | null>(null);
  const [sourceNewName, setSourceNewName] = useState("");
  const [sourceNewSearch, setSourceNewSearch] = useState("");
  const [targetNewTypeId, setTargetNewTypeId] = useState<string | null>(null);
  const [targetNewName, setTargetNewName] = useState("");
  const [targetNewSearch, setTargetNewSearch] = useState("");
  const [relBusy, setRelBusy] = useState(false);

  const openSentinel = open ? `${open.kind}` : null;
  useEffect(() => {
    if (open) {
      setKind(open.kind);
      setConceptSearch("");
      setSelectedTypeId(null);
      setSelectedStereotypeId(null);
      setName("");
      setSourceSearch("");
      setTargetSearch("");
      setSelectedSourceId(null);
      setSelectedTargetId(null);
      setSelectedRelationTypeId(null);
      setLabel("");
      setPuText("");
      setPuBusy(false);
      setPuFeedback(null);
      setSourceMode("existing");
      setTargetMode("existing");
      setSourceNewTypeId(null);
      setSourceNewName("");
      setSourceNewSearch("");
      setTargetNewTypeId(null);
      setTargetNewName("");
      setTargetNewSearch("");
      setRelBusy(false);
    }
  }, [openSentinel, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, onClose]);

  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  /**
   * Concept-tab-lijst: union van pure base-types EN stereotypes.
   * Stereotypes verschijnen als `«Naam» (Base)` regels; bij selectie
   * wordt het base-type aangemaakt + het stereotype direct toegepast.
   *
   * Polymorfe stereotypes (bv. `c4.Container` op ApplicationComponent
   * én SystemSoftware) verschijnen één keer per base als aparte regel.
   * Wildcard-stereotypes (`applies_to: ["*"]`, zoals `«Reference Object»`)
   * skippen we — die passen niet bij één-type-selectie en horen via
   * AttributePanel toegepast te worden.
   */
  const filteredTypes = useMemo<InsertItem[]>(() => {
    if (kind !== "concept") return [];
    const reg = getRegistry();
    const all = reg.listActiveElementTypes();
    const isAllowedBase = (t: ElementType) =>
      !activeViewKind || isElementTypeAllowedOnViewKind(t.id, activeViewKind);
    const allowedBases = all.filter(isAllowedBase);
    const baseById = new Map(all.map((t) => [t.id, t] as const));

    // 1. Pure base-types
    const items: InsertItem[] = allowedBases.map((t) => ({
      kind: "type",
      key: `type:${t.id}`,
      baseType: t,
    }));

    // 2. Stereotypes — één regel per (stereotype × applies_to-base).
    for (const st of reg.listStereotypes()) {
      const appliesTo = reg.appliesToTypeIds(st);
      // Skip wildcards en relation-stereotypes (applies_to wijst naar
      // relation-types, niet element-types). isAllowedBase + baseById.has
      // dekt beide gevallen af.
      for (const baseId of appliesTo) {
        if (baseId === "*") continue;
        const baseType = baseById.get(baseId);
        if (!baseType) continue; // wsl relation-stereotype → skip
        if (!isAllowedBase(baseType)) continue;
        items.push({
          kind: "stereotype",
          key: `st:${st.id}:${baseId}`,
          stereotype: st,
          baseType,
        });
      }
    }

    // 3. Filter op zoek-query
    const q = conceptSearch.trim().toLowerCase();
    const matches = (it: InsertItem): boolean => {
      if (!q) return true;
      const labelText = displayItemLabel(it, lang).toLowerCase();
      const idLower = (it.kind === "type" ? it.baseType.id : it.stereotype.id).toLowerCase();
      const baseLower = it.baseType.id.toLowerCase();
      return labelText.includes(q) || idLower.includes(q) || baseLower.includes(q);
    };

    // 4. Sort alfabetisch op de getoonde label (incl. «»-prefix voor stereotypes)
    return items
      .filter(matches)
      .sort((a, b) => displayItemLabel(a, lang).localeCompare(displayItemLabel(b, lang), lang));
  }, [kind, activeViewKind, conceptSearch, lang]);

  // Voor EndpointPicker (relatie-tab's "Nieuw endpoint") tonen we alleen
  // pure base-types — stereotype-keuze daar later. Derived van
  // filteredTypes om de filter/sort/view-kind-logica te delen.
  const endpointBaseTypes = useMemo<ElementType[]>(
    () => filteredTypes.flatMap((it) => (it.kind === "type" ? [it.baseType] : [])),
    [filteredTypes],
  );

  const diagramConcepts = useMemo<ConceptPickItem[]>(() => {
    void revision;
    if (!activeDiagramId) return [];
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return [];
    const seen = new Set<string>();
    const out: ConceptPickItem[] = [];
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (!ve.position) continue;
      if (ve.hidden === true) continue;
      const conceptId = ve.ref ?? key;
      if (seen.has(conceptId)) continue;
      const concept: Concept | undefined = store.getConcept(conceptId);
      if (!concept) continue;
      seen.add(conceptId);
      out.push({
        id: concept.id,
        typeId: concept.typeId,
        label: resolveBilingual(concept.name, lang) || t("insertRow.unnamed"),
      });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [activeDiagramId, revision, lang, t]);

  const filterConcepts = (q: string): ConceptPickItem[] => {
    const query = q.trim().toLowerCase();
    if (!query) return diagramConcepts;
    return diagramConcepts.filter(
      (c) => c.label.toLowerCase().includes(query) || c.typeId.toLowerCase().includes(query),
    );
  };
  const sourceCandidates = useMemo(
    () => filterConcepts(sourceSearch),
    [sourceSearch, diagramConcepts],
  );
  const targetCandidates = useMemo(
    () => filterConcepts(targetSearch),
    [targetSearch, diagramConcepts],
  );

  // Effectieve typeId voor source/target — uit existing-picker OF uit
  // de "nieuw"-mode's gekozen type. Beide paden converteren naar één
  // typeId zodat de matrix de juiste relatie-types kan filteren.
  const effectiveSourceTypeId: string | null = useMemo(() => {
    if (sourceMode === "new") return sourceNewTypeId;
    if (!selectedSourceId) return null;
    return useWorkspaceData.getState().store.getConcept(selectedSourceId)?.typeId ?? null;
  }, [sourceMode, sourceNewTypeId, selectedSourceId]);
  const effectiveTargetTypeId: string | null = useMemo(() => {
    if (targetMode === "new") return targetNewTypeId;
    if (!selectedTargetId) return null;
    return useWorkspaceData.getState().store.getConcept(selectedTargetId)?.typeId ?? null;
  }, [targetMode, targetNewTypeId, selectedTargetId]);

  const allowedRelationTypeIds = useMemo<string[]>(() => {
    if (!effectiveSourceTypeId || !effectiveTargetTypeId) return [];
    return validRelationTypesBetween(effectiveSourceTypeId, effectiveTargetTypeId);
  }, [effectiveSourceTypeId, effectiveTargetTypeId]);

  useEffect(() => {
    if (selectedRelationTypeId && !allowedRelationTypeIds.includes(selectedRelationTypeId)) {
      setSelectedRelationTypeId(null);
    }
  }, [selectedRelationTypeId, allowedRelationTypeIds]);

  const sourceReady =
    sourceMode === "existing"
      ? !!selectedSourceId
      : !!sourceNewTypeId && sourceNewName.trim() !== "";
  const targetReady =
    targetMode === "existing"
      ? !!selectedTargetId
      : !!targetNewTypeId && targetNewName.trim() !== "";
  const conceptSubmitDisabled = !selectedTypeId || name.trim() === "";
  const relationSubmitDisabled = !sourceReady || !targetReady || !selectedRelationTypeId || relBusy;

  const handleSubmit = async () => {
    if (kind === "concept") {
      if (conceptSubmitDisabled || !selectedTypeId) return;
      onCreateConcept(
        selectedTypeId,
        name.trim(),
        selectedStereotypeId ? [selectedStereotypeId] : undefined,
      );
      onClose();
      return;
    }
    if (relationSubmitDisabled || !selectedRelationTypeId) return;
    // Wanneer beide endpoints "existing" zijn en geen flex-callback
    // gegeven is, val terug op de oude onCreateRelation-signature.
    if (
      sourceMode === "existing" &&
      targetMode === "existing" &&
      selectedSourceId &&
      selectedTargetId &&
      !onCreateRelationFlex
    ) {
      onCreateRelation(selectedSourceId, selectedTargetId, selectedRelationTypeId, label);
      onClose();
      return;
    }
    // Anders: flex-callback (vereist voor nieuwe endpoints).
    if (!onCreateRelationFlex) return;
    const sourceArg: RelationEndpoint =
      sourceMode === "existing"
        ? { kind: "existing", id: selectedSourceId! }
        : { kind: "new", typeId: sourceNewTypeId!, name: sourceNewName.trim() };
    const targetArg: RelationEndpoint =
      targetMode === "existing"
        ? { kind: "existing", id: selectedTargetId! }
        : { kind: "new", typeId: targetNewTypeId!, name: targetNewName.trim() };
    setRelBusy(true);
    try {
      await onCreateRelationFlex(sourceArg, targetArg, selectedRelationTypeId, label);
      onClose();
    } finally {
      setRelBusy(false);
    }
  };

  if (!open) return null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-[520px] flex-col overflow-hidden rounded-md border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex border-(--ld-border) border-b bg-(--ld-raised)">
          <button
            type="button"
            className={`px-4 py-2 text-sm ${
              kind === "concept"
                ? "border-(--ld-accent) border-b-2 font-medium text-(--ld-text)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setKind("concept")}
          >
            {t("attributePanel.headerConcept")}
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm ${
              kind === "relation"
                ? "border-(--ld-accent) border-b-2 font-medium text-(--ld-text)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setKind("relation")}
          >
            {t("attributePanel.headerRelation")}
          </button>
          {onImportPlantUml && (
            <button
              type="button"
              className={`px-4 py-2 text-sm ${
                kind === "plantuml"
                  ? "border-(--ld-accent) border-b-2 font-medium text-(--ld-text)"
                  : "text-(--ld-muted) hover:text-(--ld-text)"
              }`}
              onClick={() => setKind("plantuml")}
            >
              {t("insertRow.tabPlantUml")}
            </button>
          )}
        </div>

        {kind === "concept" && (
          <div className="flex flex-col gap-3 overflow-hidden p-4">
            <input
              ref={searchRef}
              type="text"
              placeholder={t("insertRow.typeSearchPlaceholder")}
              value={conceptSearch}
              onChange={(e) => setConceptSearch(e.target.value)}
              className="rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" && filteredTypes[0]) {
                  e.preventDefault();
                  const first = filteredTypes[0];
                  setSelectedTypeId(first.baseType.id);
                  setSelectedStereotypeId(first.kind === "stereotype" ? first.stereotype.id : null);
                }
              }}
            />
            <div className="max-h-[260px] overflow-y-auto rounded border border-(--ld-border)">
              {filteredTypes.length === 0 ? (
                <div className="px-3 py-4 text-center text-(--ld-muted) text-sm italic">
                  {t("insertRow.noTypesFound")}
                </div>
              ) : (
                <ul className="divide-y divide-(--ld-rule)">
                  {filteredTypes.map((item) => {
                    const isSelected =
                      item.kind === "type"
                        ? item.baseType.id === selectedTypeId && !selectedStereotypeId
                        : item.baseType.id === selectedTypeId &&
                          item.stereotype.id === selectedStereotypeId;
                    const idText = item.kind === "type" ? item.baseType.id : item.stereotype.id;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTypeId(item.baseType.id);
                            setSelectedStereotypeId(
                              item.kind === "stereotype" ? item.stereotype.id : null,
                            );
                          }}
                          className={`w-full px-3 py-1.5 text-left text-sm hover:bg-(--ld-raised) ${
                            isSelected
                              ? "bg-(--ld-active) text-(--ld-accent) ring-1 ring-inset ring-(--ld-accent)"
                              : "text-(--ld-text)"
                          }`}
                        >
                          <span className="font-medium">{displayItemLabel(item, lang)}</span>
                          <span className="ml-2 text-xs text-(--ld-muted)">{idText}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {/* Preview-rij — toont een mini-vakje in de laag-kleur van
                het gekozen type, naast het naam-veld. Zo ziet de
                modelleur direct welke visuele "footprint" 't nieuwe
                vakje straks op canvas krijgt. Minimal versie: alleen
                kleur, geen ArchiMate-icoon of vorm-variatie. */}
            {(() => {
              const reg = getRegistry();
              const selectedType = selectedTypeId ? reg.getElementType(selectedTypeId) : null;
              const selectedStereotype = selectedStereotypeId
                ? reg.getStereotype(selectedStereotypeId)
                : null;
              const previewColor = selectedType
                ? colorForConcept({
                    typeId: selectedType.id,
                    layer: selectedType.layer,
                    ...(selectedStereotype ? { stereotypes: [selectedStereotype.id] } : {}),
                  })
                : null;
              const previewLabel = selectedType
                ? selectedStereotype
                  ? `«${resolveBilingual(selectedStereotype.label, lang)}»`
                  : resolveBilingual(selectedType.label, lang)
                : t("insertRow.preview");
              return (
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-28 shrink-0 items-center justify-center rounded border px-2 text-center text-xs font-medium"
                    style={{
                      backgroundColor: previewColor?.bg ?? "#f1f5f9",
                      borderColor: previewColor?.border ?? "#cbd5e1",
                      color: previewColor?.text ?? "#64748b",
                    }}
                    title={
                      selectedType
                        ? selectedStereotype
                          ? t("insertRow.previewStereotypeTitle", {
                              stereotype: resolveBilingual(selectedStereotype.label, lang),
                              type: resolveBilingual(selectedType.label, lang),
                              layer: selectedType.layer,
                            })
                          : t("insertRow.previewLayerTitle", { layer: selectedType.layer })
                        : t("insertRow.pickTypeFirst")
                    }
                  >
                    {previewLabel}
                  </div>
                  <input
                    type="text"
                    placeholder={t("insertRow.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !conceptSubmitDisabled) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                    className="flex-1 rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
                  />
                </div>
              );
            })()}
            <div className="flex justify-end gap-2 border-(--ld-rule) border-t pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded px-3 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
              >
                {t("insertRow.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={conceptSubmitDisabled}
                className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) disabled:bg-(--ld-active)"
              >
                {t("insertRow.insert")}
              </button>
            </div>
          </div>
        )}

        {kind === "relation" && (
          <div className="flex flex-col gap-3 overflow-hidden p-4">
            {
              <>
                <EndpointPicker
                  label={t("insertRow.source")}
                  mode={sourceMode}
                  setMode={setSourceMode}
                  // existing-pad
                  search={sourceSearch}
                  setSearch={setSourceSearch}
                  candidates={sourceCandidates}
                  selectedId={selectedSourceId}
                  setSelectedId={setSelectedSourceId}
                  // new-pad
                  newTypeId={sourceNewTypeId}
                  setNewTypeId={setSourceNewTypeId}
                  newName={sourceNewName}
                  setNewName={setSourceNewName}
                  newSearch={sourceNewSearch}
                  setNewSearch={setSourceNewSearch}
                  filteredTypes={endpointBaseTypes}
                  lang={lang}
                  flexEnabled={!!onCreateRelationFlex}
                />
                <EndpointPicker
                  label={t("insertRow.target")}
                  mode={targetMode}
                  setMode={setTargetMode}
                  search={targetSearch}
                  setSearch={setTargetSearch}
                  candidates={targetCandidates}
                  selectedId={selectedTargetId}
                  setSelectedId={setSelectedTargetId}
                  newTypeId={targetNewTypeId}
                  setNewTypeId={setTargetNewTypeId}
                  newName={targetNewName}
                  setNewName={setTargetNewName}
                  newSearch={targetNewSearch}
                  setNewSearch={setTargetNewSearch}
                  filteredTypes={endpointBaseTypes}
                  lang={lang}
                  flexEnabled={!!onCreateRelationFlex}
                />
                <div>
                  <div className="mb-1 text-xs text-(--ld-muted)">
                    {t("insertRow.relationType")}
                  </div>
                  {!sourceReady || !targetReady ? (
                    <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-muted) text-xs italic">
                      {t("insertRow.pickEndpointsFirst")}
                    </div>
                  ) : allowedRelationTypeIds.length === 0 ? (
                    <div className="rounded border border-transparent bg-(--ld-notice-bg) px-3 py-2 text-(--ld-notice-ink) text-xs">
                      {t("insertRow.noAllowedRelationTypes")}
                    </div>
                  ) : (
                    <div className="max-h-[140px] overflow-y-auto rounded border border-(--ld-border)">
                      <ul className="divide-y divide-(--ld-rule)">
                        {allowedRelationTypeIds.map((rtId) => {
                          const reg = getRegistry();
                          const rt = reg.getRelationType(rtId);
                          const labelText = rt ? resolveBilingual(rt.label, lang) : rtId;
                          const isSelected = rtId === selectedRelationTypeId;
                          return (
                            <li key={rtId}>
                              <button
                                type="button"
                                onClick={() => setSelectedRelationTypeId(rtId)}
                                className={`w-full px-3 py-1.5 text-left text-sm hover:bg-(--ld-raised) ${
                                  isSelected
                                    ? "bg-(--ld-active) text-(--ld-accent) ring-1 ring-inset ring-(--ld-accent)"
                                    : "text-(--ld-text)"
                                }`}
                              >
                                <span className="font-medium">{labelText}</span>
                                <span className="ml-2 text-xs text-(--ld-muted)">{rtId}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  placeholder={t("insertRow.labelPlaceholder")}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !relationSubmitDisabled) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  className="rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
                />
              </>
            }
            <div className="flex justify-end gap-2 border-(--ld-rule) border-t pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded px-3 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
              >
                {t("insertRow.cancel")}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={relationSubmitDisabled}
                className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) disabled:bg-(--ld-active)"
              >
                {t("insertRow.insert")}
              </button>
            </div>
          </div>
        )}

        {kind === "plantuml" && onImportPlantUml && (
          <div className="flex flex-col gap-3 overflow-hidden p-4">
            <div className="text-(--ld-muted) text-xs">{t("insertRow.plantUmlHint")}</div>
            <textarea
              value={puText}
              onChange={(e) => setPuText(e.target.value)}
              placeholder={t("insertRow.plantUmlPlaceholder")}
              rows={12}
              className="resize-y rounded border border-(--ld-muted) px-2 py-1 font-mono text-xs focus:border-(--ld-accent) focus:outline-none"
            />
            {puFeedback?.ok === true && (
              <div className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-xs">
                {t("insertRow.imported", {
                  conceptsCreated: puFeedback.diff.conceptsCreated,
                  conceptsUpdated: puFeedback.diff.conceptsUpdated,
                  relationsCreated: puFeedback.diff.relationsCreated,
                  relationsUpdated: puFeedback.diff.relationsUpdated,
                })}
              </div>
            )}
            {puFeedback?.ok === false && (
              <div className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-xs">
                {puFeedback.error}
              </div>
            )}
            <div className="flex justify-end gap-2 border-(--ld-rule) border-t pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded px-3 py-1 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
              >
                {t("insertRow.close")}
              </button>
              <button
                type="button"
                disabled={puBusy || puText.trim() === ""}
                onClick={async () => {
                  if (!onImportPlantUml) return;
                  setPuBusy(true);
                  setPuFeedback(null);
                  try {
                    const diff = await onImportPlantUml(puText);
                    setPuFeedback({ ok: true, diff });
                  } catch (err) {
                    setPuFeedback({
                      ok: false,
                      error: err instanceof Error ? err.message : String(err),
                    });
                  } finally {
                    setPuBusy(false);
                  }
                }}
                className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) disabled:bg-(--ld-active)"
              >
                {puBusy ? t("insertRow.busy") : t("insertRow.import")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EndpointPicker(props: {
  label: string;
  mode: "existing" | "new";
  setMode: (m: "existing" | "new") => void;
  // existing-pad (delegeert naar ConceptPicker)
  search: string;
  setSearch: (v: string) => void;
  candidates: ConceptPickItem[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  // new-pad
  newTypeId: string | null;
  setNewTypeId: (id: string | null) => void;
  newName: string;
  setNewName: (v: string) => void;
  newSearch: string;
  setNewSearch: (v: string) => void;
  filteredTypes: ElementType[];
  lang: "nl" | "en";
  flexEnabled: boolean;
}) {
  const {
    label,
    mode,
    setMode,
    search,
    setSearch,
    candidates,
    selectedId,
    setSelectedId,
    newTypeId,
    setNewTypeId,
    newName,
    setNewName,
    newSearch,
    setNewSearch,
    filteredTypes,
    lang,
    flexEnabled,
  } = props;
  const { t } = useTranslation();
  // Filter de gedeelde filteredTypes-lijst nogmaals lokaal op de newSearch
  // van deze picker — anders zou typen in source's search ook target's
  // lijst filteren.
  const newCandidates = useMemo<ElementType[]>(() => {
    const q = newSearch.trim().toLowerCase();
    if (!q) return filteredTypes;
    return filteredTypes.filter((t) => {
      const labelNl = (t.label.nl ?? "").toLowerCase();
      const labelEn = (t.label.en ?? "").toLowerCase();
      return labelNl.includes(q) || labelEn.includes(q) || t.id.toLowerCase().includes(q);
    });
  }, [filteredTypes, newSearch]);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="text-xs text-(--ld-muted)">{label}</div>
        {flexEnabled && (
          <div className="flex overflow-hidden rounded border border-(--ld-border) text-xs">
            <button
              type="button"
              onClick={() => setMode("existing")}
              className={`px-2 py-0.5 ${
                mode === "existing"
                  ? "bg-(--ld-primary) text-(--ld-primary-ink)"
                  : "bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
              }`}
            >
              {t("insertRow.existing")}
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              className={`px-2 py-0.5 ${
                mode === "new"
                  ? "bg-(--ld-primary) text-(--ld-primary-ink)"
                  : "bg-(--ld-surface) text-(--ld-muted) hover:bg-(--ld-raised)"
              }`}
            >
              {t("insertRow.new")}
            </button>
          </div>
        )}
      </div>
      {mode === "existing" ? (
        <ConceptPicker
          label={label}
          search={search}
          setSearch={setSearch}
          candidates={candidates}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          inline
        />
      ) : (
        <div className="flex flex-col gap-1">
          <input
            type="text"
            placeholder={t("insertRow.typeSearchShort")}
            value={newSearch}
            onChange={(e) => setNewSearch(e.target.value)}
            className="rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
          />
          <div className="max-h-[120px] overflow-y-auto rounded border border-(--ld-border)">
            {newCandidates.length === 0 ? (
              <div className="px-3 py-2 text-center text-(--ld-muted) text-xs italic">
                {t("insertRow.noTypesFound")}
              </div>
            ) : (
              <ul className="divide-y divide-(--ld-rule)">
                {newCandidates.map((t) => {
                  const isSelected = t.id === newTypeId;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => setNewTypeId(t.id)}
                        className={`w-full px-3 py-1 text-left text-xs hover:bg-(--ld-raised) ${
                          isSelected
                            ? "bg-(--ld-active) text-(--ld-accent) ring-1 ring-inset ring-(--ld-accent)"
                            : "text-(--ld-text)"
                        }`}
                      >
                        <span className="font-medium">{resolveBilingual(t.label, lang)}</span>
                        <span className="ml-2 text-xs text-(--ld-muted)">{t.id}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <input
            type="text"
            placeholder={t("insertRow.newNamePlaceholder", { label: label.toLowerCase() })}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
          />
        </div>
      )}
    </div>
  );
}

function ConceptPicker(props: {
  label: string;
  search: string;
  setSearch: (v: string) => void;
  candidates: ConceptPickItem[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  /** Als inline=true skipt 'ie de eigen label-header — wordt al getoond
   *  door de wrapper EndpointPicker. */
  inline?: boolean;
}) {
  const { label, search, setSearch, candidates, selectedId, setSelectedId, inline } = props;
  const { t } = useTranslation();
  return (
    <div>
      {!inline && (
        <div className="mb-1 flex items-center justify-between">
          <div className="text-xs text-(--ld-muted)">{label}</div>
          {selectedId && (
            <div className="text-xs text-(--ld-accent)">
              {candidates.find((c) => c.id === selectedId)?.label ?? t("insertRow.notVisible")}
            </div>
          )}
        </div>
      )}
      <input
        type="text"
        placeholder={t("insertRow.searchPlaceholder", { label: label.toLowerCase() })}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-1 w-full rounded border border-(--ld-muted) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
      />
      <div className="max-h-[120px] overflow-y-auto rounded border border-(--ld-border)">
        {candidates.length === 0 ? (
          <div className="px-3 py-2 text-center text-(--ld-muted) text-xs italic">
            {t("insertRow.noResults")}
          </div>
        ) : (
          <ul className="divide-y divide-(--ld-rule)">
            {candidates.map((c) => {
              const isSelected = c.id === selectedId;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full px-3 py-1 text-left text-xs hover:bg-(--ld-raised) ${
                      isSelected
                        ? "bg-(--ld-active) text-(--ld-accent) ring-1 ring-inset ring-(--ld-accent)"
                        : "text-(--ld-text)"
                    }`}
                  >
                    <span className="font-medium">{c.label}</span>
                    <span className="ml-2 text-xs text-(--ld-muted)">{c.typeId}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
